import React, { useState, useEffect } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import {
  Paper,
  Grid,
  TextField,
  Typography,
  Box,
  TableContainer,
  Dialog,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import {
  StyledButton,
  StyledAlert,
  StyledSnackbar,
  inputStyle,
  StyledAgGridContainer,
  StyledTypography,
} from "components/StyledComponents/styledComponents.js";
import { CustomPaper } from "components/Styles/globalStyles";
import Cookie from "js-cookie";
import URLs from "constants/urls";
import { ViewButton } from "components/StyledComponents/styledComponents.js";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useMaterialUIController } from "context";

export default function ViewPaySlips() {
  const [controller] = useMaterialUIController();
  const { user } = controller;
  const [employeeRecordId, setEmployeeRecordId] = useState(null);
  const [paySlipsData, setPaySlipsData] = useState([]);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const hrms_token = Cookie.get("hrms_token");
  const MInput = inputStyle();

  const fetchEmployeeRecordId = async (employeeId) => {
    try {
      const response = await fetch(`${URLs.getEmpPayRollData}${employeeId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });
      if (!response.ok) {
        console.warn("Employee details fetch warning:", response.status);
        return;
      }
      const data = await response.json();
      if (data && data.employeeRecordId) {
        setEmployeeRecordId(data.employeeRecordId);
        fetchPaySlipsData(data.employeeRecordId, data.companyId || 0);
      } else {
        console.warn("No employee record ID found in response");
      }
    } catch (error) {
      console.warn("Error fetching employee details:", error);
    }
  };

  // Fetch payslips data
  const fetchPaySlipsData = async (empRecordId, companyId) => {
    if (!empRecordId) {
      console.warn("No employee record ID available");
      return;
    }
    setLoading(true);
    try {
      console.log(`Fetching payslips for empRecordId: ${empRecordId}, companyId: ${companyId}`);
      const response = await fetch(`${URLs.hrmsViewPayslipsById}/${companyId}/${empRecordId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        console.warn("Payslips data fetch warning:", response.status);
        setPaySlipsData([]);
        return;
      }

      const data = await response.json();
      if (Array.isArray(data)) {
        console.log("Payslips data received:", data);
        setPaySlipsData(data);
      } else {
        console.warn("Unexpected data format:", data);
        setPaySlipsData([]);
      }
    } catch (error) {
      console.warn("Error fetching payslips:", error);
      setPaySlipsData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.employeeId) {
      fetchEmployeeRecordId(user.employeeId);
    }
  }, [user]);

  const setAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "payslip.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleViewPayslip = async (payPeriodId, employeeRecordId) => {
    try {
      setLoading(true);
      const response = await fetch(`${URLs.hrmsPayslipPdf}/${payPeriodId}/${employeeRecordId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to generate payslip");
      }

      const blob = await response.blob();
      if (blob.size === 0) {
        throw new Error("Generated PDF is empty");
      }

      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setDialogOpen(true);
      setAlert("Payslip generated successfully", "success");
    } catch (error) {
      console.error("Error generating payslip:", error);
      setAlert(error.message || "Failed to generate payslip", "error");
    } finally {
      setLoading(false);
    }
  };

  const columnDefs = [
    {
      field: "periodName",
      headerName: "Month",
      filter: true,
      sortable: true,
      flex: 1,
    },
    {
      field: "employeeName",
      headerName: "Employee Name",
      filter: true,
      sortable: true,
      flex: 1.2,
    },
    {
      field: "noOfWorkingDays",
      headerName: "Working Days",
      filter: true,
      sortable: true,
      flex: 1,
      cellStyle: { textAlign: "center" },
    },
    {
      field: "noOfAttendanceDays",
      headerName: "Attendance",
      filter: true,
      sortable: true,
      flex: 1,
      cellStyle: { textAlign: "center" },
    },
    {
      field: "noOfLeaves",
      headerName: "Leaves",
      filter: true,
      sortable: true,
      flex: 1,
      cellStyle: { textAlign: "center" },
    },
    {
      field: "lossOfPayDays",
      headerName: "LOP",
      filter: true,
      sortable: true,
      flex: 1,
      cellStyle: { textAlign: "center" },
    },
    {
      field: "totalAdditions",
      headerName: "Addition",
      filter: true,
      sortable: true,
      flex: 1,
      cellStyle: { textAlign: "right" },
      valueFormatter: (params) =>
        `₹ ${params.value?.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
    {
      field: "totalDeductions",
      headerName: "Deduction",
      filter: true,
      sortable: true,
      flex: 1,
      cellStyle: { textAlign: "right" },
      valueFormatter: (params) =>
        `₹ ${params.value?.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
    {
      field: "netPay",
      headerName: "Net Pay",
      filter: true,
      sortable: true,
      flex: 1,
      cellStyle: { textAlign: "right", fontWeight: "bold" },
      valueFormatter: (params) =>
        `₹ ${params.value?.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
    {
      headerName: "Actions",
      field: "actions",
      sortable: false,
      filter: false,
      flex: 0.8,
      cellRenderer: (params) => (
        <ViewButton
          onClick={() => handleViewPayslip(params.data.payPeriodId, params.data.employeeRecordId)}
          sx={{ minWidth: "40px", padding: "4px" }}
        >
          <VisibilityIcon fontSize="small" />
        </ViewButton>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 100px)",
          overflow: "hidden",
          backgroundColor: "transparent",
        }}
      >
        {/* <StyledTypography sx={{ p: 2 }}> Pay Slips</StyledTypography> */}

        <Box
          sx={{
            flex: 1,
            backgroundColor: "#ffffff",
            borderRadius: "15px",
            m: 2,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <StyledAgGridContainer
            className="ag-theme-alpine"
            sx={{
              flex: 1,
              width: "100%",
              "& .ag-root": { border: "none" },
              "& .ag-header": { backgroundColor: MInput.a },
              "& .ag-header-cell": { color: "#ffffff" },
              "& .ag-row-odd": { backgroundColor: "#f5f5f5" },
              "& .ag-row-even": { backgroundColor: "#ffffff" },
              "& .ag-body": { overflow: "hidden" },
              "& .ag-body-viewport": { overflow: "hidden" },
            }}
          >
            <AgGridReact
              columnDefs={columnDefs}
              rowData={paySlipsData}
              defaultColDef={{
                editable: false,
                sortable: true,
                filter: true,
                resizable: true,
                minWidth: 100,
              }}
              pagination={true}
              paginationPageSize={8}
              suppressScrollOnNewData={true}
              suppressHorizontalScroll={true}
              domLayout="normal"
              overlayNoRowsTemplate=""
            />
          </StyledAgGridContainer>
        </Box>

        <StyledSnackbar
          open={open}
          autoHideDuration={5000}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <StyledAlert onClose={handleClose} severity={alertType}>
            {message}
          </StyledAlert>
        </StyledSnackbar>
      </Box>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogContent>
          {pdfUrl && (
            <div style={{ height: "80vh", display: "flex", flexDirection: "column" }}>
              <iframe
                src={pdfUrl}
                style={{ width: "100%", height: "100%", border: "none" }}
                title="Payslip Preview"
              />
              <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                <StyledButton onClick={handleDownload} variant="contained">
                  Download
                </StyledButton>
              </Box>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
